import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import api, { apiErr } from "@/lib/api";
import { Loader, PageHeader, btn } from "@/components/Page";

const DEFAULT_ENTRY = {
  id: "",
  label: "",
  type: "gold",
  amount: 100,
  weight: 1,
  enabled: true,
  color: "#b45309",
  fragment_kind: "weapon",
  fragment_rarity: "common",
  consumable_id: "campaign_stamina_potion",
};

export default function WheelFortunaAdmin() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const response = await api.get("/daily-ludus/fortuna/admin");
      setData(response.data);
    } catch (error) {
      toast.error(apiErr(error, "Could not load Wheel of Fortuna admin settings"));
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateEntry = (index, field, value) => {
    setData((current) => {
      const next = JSON.parse(JSON.stringify(current));
      next.config.entries[index][field] = ["amount", "weight"].includes(field) ? Number(value || 0) : value;
      return next;
    });
  };

  const moveEntry = (index, direction) => {
    setData((current) => {
      const target = index + direction;
      if (target < 0 || target >= current.config.entries.length) return current;
      const entries = [...current.config.entries];
      [entries[index], entries[target]] = [entries[target], entries[index]];
      return { ...current, config: { ...current.config, entries } };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        reset_hour_local: Number(data.config.reset_hour_local || 0),
        entries: data.config.entries,
      };
      const response = await api.put("/daily-ludus/fortuna/admin", payload);
      setData((current) => ({ ...current, config: response.data.config }));
      toast.success("Wheel of Fortuna settings saved.");
    } catch (error) {
      toast.error(apiErr(error, "Could not save Wheel of Fortuna settings"));
    } finally {
      setSaving(false);
    }
  };

  if (!data) return <Loader label="Loading Wheel of Fortuna admin…" />;

  return (
    <div data-testid="wheel-fortuna-admin-page" className="space-y-6">
      <PageHeader
        title="Wheel of Fortuna Admin"
        subtitle="Edit reward entries, slice weights, and the free-spin daily reset timing."
        action={<Link data-testid="wheel-fortuna-admin-back-link" to="/admin" className={btn("ghost")}>Back to Admin</Link>}
      />

      <section data-testid="wheel-fortuna-admin-settings" className="rounded-sm border border-amber-900/35 bg-zinc-950/70 p-5">
        <div className="grid gap-4 md:grid-cols-[0.4fr_1fr]">
          <label className="text-sm text-zinc-400">
            Daily reset hour (server clock)
            <input
              data-testid="wheel-fortuna-reset-hour-input"
              type="number"
              min="0"
              max="23"
              value={data.config.reset_hour_local}
              onChange={(event) => setData((current) => ({ ...current, config: { ...current.config, reset_hour_local: Number(event.target.value || 0) } }))}
              className="mt-1 w-full rounded-sm border border-zinc-800 bg-zinc-950 px-3 py-2 text-zinc-100 outline-none"
            />
          </label>
          <div className="rounded-sm border border-zinc-800 bg-zinc-950/50 p-4 text-sm text-zinc-400">
            Players receive exactly one free spin per reset window. This hour controls when the server day rolls over for Wheel of Fortuna.
          </div>
        </div>
      </section>

      <div className="flex justify-end">
        <button data-testid="wheel-fortuna-add-entry-button" type="button" onClick={() => setData((current) => ({ ...current, config: { ...current.config, entries: [...current.config.entries, { ...DEFAULT_ENTRY, id: `custom-${Date.now()}` }] } }))} className={btn("ghost")}>
          Add Reward Entry
        </button>
      </div>

      <section data-testid="wheel-fortuna-admin-entry-list" className="space-y-4">
        {data.config.entries.map((entry, index) => (
          <div key={`${entry.id}-${index}`} data-testid={`wheel-fortuna-admin-entry-${index}`} className="rounded-sm border border-amber-900/35 bg-zinc-950/70 p-5 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="font-cinzel text-xl text-amber-200">Reward Slice #{index + 1}</div>
              <div className="flex flex-wrap gap-2">
                <button data-testid={`wheel-fortuna-move-entry-up-${index}`} type="button" onClick={() => moveEntry(index, -1)} disabled={index === 0} className={btn("ghost")}>Move Up</button>
                <button data-testid={`wheel-fortuna-move-entry-down-${index}`} type="button" onClick={() => moveEntry(index, 1)} disabled={index === data.config.entries.length - 1} className={btn("ghost")}>Move Down</button>
                <button data-testid={`wheel-fortuna-remove-entry-${index}`} type="button" onClick={() => setData((current) => ({ ...current, config: { ...current.config, entries: current.config.entries.filter((_, entryIndex) => entryIndex !== index) } }))} className={btn("ghost")}>Remove</button>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <label className="text-sm text-zinc-400">
                Label
                <input data-testid={`wheel-fortuna-label-${index}`} value={entry.label} onChange={(event) => updateEntry(index, "label", event.target.value)} className="mt-1 w-full rounded-sm border border-zinc-800 bg-zinc-950 px-3 py-2 text-zinc-100 outline-none" />
              </label>
              <label className="text-sm text-zinc-400">
                Reward type
                <select data-testid={`wheel-fortuna-type-${index}`} value={entry.type} onChange={(event) => updateEntry(index, "type", event.target.value)} className="mt-1 w-full rounded-sm border border-zinc-800 bg-zinc-950 px-3 py-2 text-zinc-100 outline-none">
                  {data.options.reward_types.map((type) => <option key={type} value={type}>{type}</option>)}
                </select>
              </label>
              <label className="text-sm text-zinc-400">
                Amount
                <input data-testid={`wheel-fortuna-amount-${index}`} type="number" min="1" value={entry.amount} onChange={(event) => updateEntry(index, "amount", event.target.value)} className="mt-1 w-full rounded-sm border border-zinc-800 bg-zinc-950 px-3 py-2 text-zinc-100 outline-none" />
              </label>
              <label className="text-sm text-zinc-400">
                Weight
                <input data-testid={`wheel-fortuna-weight-${index}`} type="number" min="0" value={entry.weight} onChange={(event) => updateEntry(index, "weight", event.target.value)} className="mt-1 w-full rounded-sm border border-zinc-800 bg-zinc-950 px-3 py-2 text-zinc-100 outline-none" />
              </label>
              <label className="text-sm text-zinc-400">
                Slice color
                <input data-testid={`wheel-fortuna-color-${index}`} value={entry.color} onChange={(event) => updateEntry(index, "color", event.target.value)} className="mt-1 w-full rounded-sm border border-zinc-800 bg-zinc-950 px-3 py-2 text-zinc-100 outline-none" />
              </label>
              <label className="text-sm text-zinc-400">
                Fragment kind
                <select data-testid={`wheel-fortuna-fragment-kind-${index}`} value={entry.fragment_kind || "weapon"} onChange={(event) => updateEntry(index, "fragment_kind", event.target.value)} className="mt-1 w-full rounded-sm border border-zinc-800 bg-zinc-950 px-3 py-2 text-zinc-100 outline-none">
                  {data.options.fragment_kinds.map((kind) => <option key={kind} value={kind}>{kind}</option>)}
                </select>
              </label>
              <label className="text-sm text-zinc-400">
                Fragment rarity
                <select data-testid={`wheel-fortuna-fragment-rarity-${index}`} value={entry.fragment_rarity || "common"} onChange={(event) => updateEntry(index, "fragment_rarity", event.target.value)} className="mt-1 w-full rounded-sm border border-zinc-800 bg-zinc-950 px-3 py-2 text-zinc-100 outline-none">
                  {data.options.fragment_rarities.map((rarity) => <option key={rarity} value={rarity}>{rarity}</option>)}
                </select>
              </label>
              <label className="text-sm text-zinc-400">
                Consumable reward
                <select data-testid={`wheel-fortuna-consumable-${index}`} value={entry.consumable_id || "campaign_stamina_potion"} onChange={(event) => updateEntry(index, "consumable_id", event.target.value)} className="mt-1 w-full rounded-sm border border-zinc-800 bg-zinc-950 px-3 py-2 text-zinc-100 outline-none">
                  {data.options.consumable_options.map((option) => <option key={option} value={option}>{option}</option>)}
                </select>
              </label>
            </div>

            <label data-testid={`wheel-fortuna-enabled-${index}`} className="inline-flex items-center gap-2 text-sm text-zinc-300">
              <input type="checkbox" checked={!!entry.enabled} onChange={(event) => updateEntry(index, "enabled", event.target.checked)} />
              Enabled on the wheel
            </label>
          </div>
        ))}
      </section>

      <section data-testid="wheel-fortuna-admin-recent-wins" className="rounded-sm border border-amber-900/35 bg-zinc-950/70 p-5">
        <div className="font-cinzel text-xl text-amber-200">Recent Server Wins</div>
        <div className="mt-4 space-y-2">
          {(data.recent_wins?.items || []).map((row) => (
            <div key={row.id} data-testid={`wheel-fortuna-admin-win-${row.id}`} className="rounded-sm border border-zinc-800 bg-zinc-950/50 px-3 py-2 text-sm text-zinc-300">
              {row.user_name} · {row.reward_label}
            </div>
          ))}
        </div>
      </section>

      <div className="flex justify-end">
        <button data-testid="wheel-fortuna-save-button" type="button" onClick={save} disabled={saving} className={btn()}>
          {saving ? "Saving…" : "Save Wheel Settings"}
        </button>
      </div>
    </div>
  );
}